import moment from 'moment-timezone';
import type { Organizer, Event, TicketConfig, Venue } from '@tickethead/sdk';
import {
  LocationJsonLd,
  OrganizerJsonLd,
  OfferJsonLd,
  ItemAvailability,
  AggregateOfferJsonLd,
  EventJsonLd,
  EventStatus,
  AttendanceMode
} from '~/types/json-ld';

export function mapToLocationJsonLd(venue: Venue): LocationJsonLd {
  if (!venue) return undefined;
  return {
    '@type': 'Place',
    name: venue?.name,
    address: {
      '@type': 'PostalAddress',
      streetAddress: venue?.address?.street1,
      addressLocality: venue?.address?.city,
      postalCode: venue?.address?.zip,
      addressRegion: venue?.address?.state,
      addressCountry: venue?.address?.country
    }
  };
}

export function mapToOrganizerJsonLd(organizer: Organizer): OrganizerJsonLd {
  if (!organizer) return undefined;
  return {
    '@type': 'Organization',
    name: organizer.name,
    url: organizer?.url
  };
}

function mapToOfferJsonLd(
  ticketConfig: TicketConfig,
  url: string
): OfferJsonLd {
  const saleStart = moment(ticketConfig.startSaleAt).format();
  const saleEnd = moment(ticketConfig.endSaleAt).format();
  return {
    '@type': 'Offer',
    url,
    price: ticketConfig.faceValue,
    availability:
      ticketConfig.availableTickets > 0
        ? ItemAvailability.InStock
        : ItemAvailability.SoldOut,
    availabilityStarts: saleStart,
    availabilityEnds: saleEnd,
    validFrom: saleStart,
    validThrough: saleEnd,
    priceCurrency: ticketConfig.currency
  };
}

function mapToAggregateOfferJsonLd(
  offers: OfferJsonLd[] = []
): AggregateOfferJsonLd {
  const offersAvailable = offers.filter(
    (offer) => offer.availability === ItemAvailability.InStock
  );
  const prices = offersAvailable.map((offer) => {
    if (typeof offer.price === 'number') return offer.price;
    return parseFloat(offer.price);
  });

  const aggregateOffer: AggregateOfferJsonLd = {
    '@type': 'AggregateOffer',
    offerCount: offers.length,
    offers
  };

  if (offersAvailable.length === 0) {
    // When no offers are available, there is nothing to calculate further
    return aggregateOffer;
  }

  return {
    ...aggregateOffer,
    availability: ItemAvailability.InStock,
    highPrice: Math.max(...prices),
    lowPrice: Math.min(...prices),
    priceCurrency: offersAvailable[0].priceCurrency,
    url: offersAvailable[0].url
  };
}

export function mapToEventJsonLd(
  event: Event,
  eventUrl: string,
  location: LocationJsonLd,
  organizer: OrganizerJsonLd
): EventJsonLd {
  // The main image is 16x9
  // The set covers the case where the image links were edited manually
  // It also covers external events
  const images = new Set([
    event.mainImageUri,
    event.mainImageUri.replace('16x9', '4x3'),
    event.mainImageUri.replace('16x9', '1x1')
  ]);
  const image = [...images.values()];

  let offers = [];
  if (event.ticketConfig) {
    offers = event.ticketConfig.map((ticketConfig) =>
      mapToOfferJsonLd(ticketConfig, eventUrl)
    );
  }

  let eventStatus = EventStatus.Scheduled;
  if (event.status === 'canceled') {
    eventStatus = EventStatus.Cancelled;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    description: event.description,
    startDate: moment(event.startAt).format(),
    endDate: moment(event.endAt).format(),
    doorTime: moment(event.gateTime).format(),
    eventAttendanceMode: AttendanceMode.Offline,
    eventStatus,
    location,
    image,
    offers: mapToAggregateOfferJsonLd(offers),
    organizer
  };
}
